import React, { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Upload, Camera, Copy, QrCode, X, MessageCircle, RotateCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { brandFor } from '../lib/brands';

// Small helper — same size formatter as the rest of the module
const readEntries = (dir) => new Promise((res) => { const r = dir.createReader(); const all = []; const next = () => r.readEntries((b) => (b.length ? (all.push(...b), next()) : res(all)), () => res(all)); next(); });
const walk = async (entry) => (entry.isFile ? [await new Promise((r) => entry.file(r, () => r(null)))] : (await Promise.all((await readEntries(entry)).map(walk))).flat());
const filesFromDrop = async (dt) => { const entries = Array.from(dt.items || []).map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null)).filter(Boolean); if (!entries.length) return Array.from(dt.files); return (await Promise.all(entries.map(walk))).flat().filter(Boolean); };


const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${units[i]}`;
};

// One-file resumable upload against a Google Drive session URL.
// Follows the same Google resumable-upload protocol as the main TransfersPage
// uploader — on ANY transient error (network, timeout, 5xx), FIRST query the
// SAME session URL with `Content-Range: bytes */TOTAL` to learn Google's actual
// state, THEN resume from that offset. Never create a new session — that would
// duplicate the file in Drive. The final chunk gets a longer timeout because
// Google's server-side commit can legitimately take 30-60s.
const eventUploadOne = async (file, uploadUrl, onProgress) => {
  const CHUNK = 4 * 1024 * 1024;
  const MAX_RETRIES = 8;
  const MIN_BACKOFF_MS = 1500;
  const CHUNK_TIMEOUT_MS = 5 * 60 * 1000;
  const FINAL_CHUNK_TIMEOUT_MS = 15 * 60 * 1000;
  const STATUS_QUERY_TIMEOUT_MS = 60 * 1000;

  let position = 0;

  const putChunk = (start, end, isFinal) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Range', `bytes ${start}-${end - 1}/${file.size}`);
    xhr.timeout = isFinal ? FINAL_CHUNK_TIMEOUT_MS : CHUNK_TIMEOUT_MS;
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && onProgress) {
        const bytesUploaded = start + evt.loaded;
        onProgress(Math.min(99, Math.round((bytesUploaded / file.size) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status === 308) {
        const range = xhr.getResponseHeader('Range');
        resolve({ done: false, next: range ? parseInt(range.split('-')[1], 10) + 1 : end });
      } else if (xhr.status === 200 || xhr.status === 201) {
        try { resolve({ done: true, meta: JSON.parse(xhr.responseText) }); }
        catch { reject({ transient: true, message: 'Drive returned invalid JSON' }); }
      } else if (xhr.status === 404 || xhr.status === 410) {
        reject({ transient: true, sessionGone: true, message: 'Session expired' });
      } else if (xhr.status >= 500 && xhr.status < 600) {
        reject({ transient: true, message: `Drive ${xhr.status}` });
      } else if (xhr.status === 408 || xhr.status === 429) {
        reject({ transient: true, message: `Drive ${xhr.status}` });
      } else if (xhr.status === 0) {
        reject({ transient: true, networkError: true, message: 'Network error' });
      } else {
        reject(new Error(`Drive ${xhr.status}: ${xhr.responseText || 'upload error'}`));
      }
    };
    xhr.onerror = () => reject({ transient: true, networkError: true, message: 'Network error' });
    xhr.ontimeout = () => reject({ transient: true, message: 'Timeout' });
    xhr.send(file.slice(start, end));
  });

  // Status query: empty PUT with `bytes */TOTAL`. Tells us if Google already
  // committed the file (200/201), where we're up to (308 + Range header), or if
  // the session is gone (404/410). This is the KEY primitive that prevents
  // duplicate uploads on final-chunk timeouts.
  const queryStatus = () => new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Range', `bytes */${file.size}`);
    xhr.timeout = STATUS_QUERY_TIMEOUT_MS;
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const meta = JSON.parse(xhr.responseText);
          resolve({ status: 'done', meta });
        } catch { resolve({ status: 'unknown' }); }
      } else if (xhr.status === 308) {
        const range = xhr.getResponseHeader('Range');
        const pos = range ? parseInt(range.split('-')[1], 10) + 1 : 0;
        resolve({ status: 'incomplete', position: pos });
      } else if (xhr.status === 404 || xhr.status === 410) {
        resolve({ status: 'session_gone' });
      } else {
        resolve({ status: 'unknown' });
      }
    };
    xhr.onerror = () => resolve({ status: 'unknown' });
    xhr.ontimeout = () => resolve({ status: 'unknown' });
    xhr.send(new Blob([]));
  });

  let attempts = 0;
  if (onProgress) onProgress(0);

  while (position < file.size) {
    const end = Math.min(position + CHUNK, file.size);
    const isFinal = end === file.size;
    try {
      const res = await putChunk(position, end, isFinal);
      attempts = 0;
      if (res.done) {
        if (onProgress) onProgress(100);
        return res.meta;
      }
      position = res.next;
      if (onProgress) onProgress(Math.round((position / file.size) * 100));
    } catch (err) {
      attempts += 1;
      if (!err?.transient || attempts > MAX_RETRIES) {
        throw new Error(err?.message || 'Upload failed');
      }
      // Query the session BEFORE any wait/retry — if Google already committed
      // (common on final-chunk network timeouts), return immediately.
      const s = await queryStatus();
      if (s.status === 'done') {
        if (onProgress) onProgress(100);
        return s.meta;
      }
      if (s.status === 'session_gone') {
        throw new Error('Upload session expired — please try again with a fresh session');
      }
      if (s.status === 'incomplete') {
        position = s.position;
        if (onProgress) onProgress(Math.round((position / file.size) * 100));
      }
      // Backoff before next attempt (whether we resumed from a new position or not)
      const backoff = MIN_BACKOFF_MS * Math.pow(1.5, attempts - 1) + Math.round(Math.random() * 500);
      await new Promise(r => setTimeout(r, backoff));
    }
  }

  // Loop exited with position === file.size but no `done`. Do a final status
  // query — Google may have committed but our last PUT didn't return 200.
  const final = await queryStatus();
  if (final.status === 'done') {
    if (onProgress) onProgress(100);
    return final.meta;
  }
  throw new Error('Upload finished but Drive did not confirm the file');
};

// Build a wa.me URL. Digits only, no + prefix (wa.me strips it anyway).
const waLink = (number, message) => {
  const digits = (number || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
};

// Card showing a QR + link + WhatsApp send. Used both for the single-photo
// success view and (inside a modal) for each row in batch mode.
const QRCard = ({ shareLink, fileName, brand, onDismiss }) => {
  const [copied, setCopied] = useState(false);
  const [waNumber, setWaNumber] = useState('');
  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success('Link copied');
  };
  const wa = waLink(waNumber, `Here's your photo: ${shareLink}`);
  const openWa = () => {
    if (!wa) return toast.error('Enter a phone number first');
    window.open(wa, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-6" data-testid="event-qr-card">
      <div className="flex flex-col items-center gap-4">
        <div className="p-3 bg-white rounded-lg" data-testid="event-qr">
          <QRCodeSVG value={shareLink} size={220} level="M" includeMargin={false} />
        </div>
        <div className="w-full text-center">
          <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Scan to download</div>
          <div className="text-sm font-medium text-white truncate" title={fileName}>{fileName}</div>
        </div>
        <div className="w-full flex gap-2">
          <Input readOnly value={shareLink} className="bg-black/40 border-white/10 font-mono text-xs" data-testid="event-share-link" />
          <Button variant="outline" onClick={copyLink} data-testid="event-copy-link">
            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <div className="w-full space-y-2">
          <Label className="text-xs text-white/60 flex items-center gap-1.5">
            <MessageCircle className="w-3.5 h-3.5" />
            Send via WhatsApp (optional)
          </Label>
          <div className="flex gap-2">
            <Input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="+91 98765 43210"
              className="bg-white/5 border-white/10"
              data-testid="event-wa-number"
            />
            <Button
              onClick={openWa}
              disabled={!waNumber.replace(/\D/g, '')}
              className="shrink-0"
              style={{ background: brand.accent, color: brand.accentText }}
              data-testid="event-wa-send">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Send
            </Button>
          </div>
          <div className="text-[10px] text-white/40">
            Opens WhatsApp with the link pre-filled — you still tap Send inside WhatsApp.
          </div>
        </div>
        {onDismiss && (
          <Button variant="ghost" onClick={onDismiss} className="w-full mt-2" data-testid="event-dismiss">
            Close
          </Button>
        )}
      </div>
    </div>
  );
};

const EventModePanel = ({ accountKey = 'witty_shadows' }) => {
  const brand = brandFor(accountKey);

  // Two sub-modes within Event Mode
  const [mode, setMode] = useState('single'); // 'single' | 'batch'

  // Common: shared factory for a per-file Drive upload → finalize round-trip
  // Each call yields ONE independent transfer record (1 file per transfer).
  const uploadOneFileAsTransfer = async (file, onProgress) => {
    // 1) Create session (transfer_id: null → backend creates a new transfer)
    const sess = await api.post('/transfers/upload-session', {
      account_key: accountKey,
      transfer_id: null,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || 'application/octet-stream',
    });
    const transferId = sess.data.transfer_id;
    const uploadUrl = sess.data.upload_url;

    // 2) PUT the bytes
    const meta = await eventUploadOne(file, uploadUrl, onProgress);
    if (onProgress) onProgress(100);

    // 3) Finalize with 1-day expiry
    const fin = await api.post('/transfers/finalize', {
      transfer_id: transferId,
      title: null,
      message: null,
      expiry_days: 1,
      recipient_emails: [],
      files: [{
        drive_file_id: meta.id,
        name: file.name,
        size: file.size,
        mime_type: file.type || 'application/octet-stream',
      }],
    });
    return {
      transferId,
      shareLink: fin.data.share_link,
      shareToken: fin.data.share_token,
      fileName: file.name,
      fileSize: file.size,
    };
  };

  // ---------- SINGLE-PHOTO MODE ----------
  const singleInputRef = useRef(null);
  const [singleFile, setSingleFile] = useState(null);
  const [singleBusy, setSingleBusy] = useState(false);
  const [singleProgress, setSingleProgress] = useState(0);
  const [singleError, setSingleError] = useState(null);
  const [singleResult, setSingleResult] = useState(null); // { shareLink, ... }

  const startSingleUpload = async () => {
    if (!singleFile) return toast.error('Pick or capture a photo first');
    setSingleBusy(true);
    setSingleError(null);
    setSingleProgress(0);
    setSingleResult(null);
    try {
      const r = await uploadOneFileAsTransfer(singleFile, setSingleProgress);
      setSingleResult(r);
      toast.success('Photo uploaded — QR ready');
    } catch (e) {
      setSingleError(e?.response ? formatApiError(e) : (e.message || 'Upload failed'));
      toast.error('Upload failed');
    } finally {
      setSingleBusy(false);
    }
  };

  const resetSingle = () => {
    setSingleFile(null);
    setSingleBusy(false);
    setSingleProgress(0);
    setSingleError(null);
    setSingleResult(null);
    if (singleInputRef.current) singleInputRef.current.value = '';
  };

  // ---------- BATCH MODE ----------
  const batchInputRef = useRef(null);
  const batchFolderInputRef = useRef(null);
  const [batchFiles, setBatchFiles] = useState([]); // File[]
  const [batchBusy, setBatchBusy] = useState(false);
  // rows: [{ fileName, fileSize, status:'pending'|'uploading'|'done'|'error', progress, shareLink?, error?, waNumber }]
  const [batchRows, setBatchRows] = useState([]);
  const [qrModal, setQrModal] = useState(null); // { row } — for showing an enlarged QR

  const stageBatch = (fileList) => {
    const arr = Array.from(fileList).filter(f => f && (f.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif|tiff?)$/i.test(f.name)) && !f.name.startsWith('.'));
    setBatchFiles(prev => [...prev, ...arr]);
  };

  const removeBatchFile = (idx) => {
    setBatchFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const startBatchUpload = async () => {
    if (batchFiles.length === 0) return toast.error('Add at least one photo');
    setBatchBusy(true);
    // Seed rows
    const initial = batchFiles.map((f) => ({
      fileName: f.name, fileSize: f.size, status: 'pending', progress: 0,
      shareLink: null, error: null, waNumber: '',
    }));
    setBatchRows(initial);
    // Sequential (1 at a time) — Event Mode is one-photo-per-transfer, so we want
    // predictable ordering and low network pressure. Small photos finish fast.
    for (let i = 0; i < batchFiles.length; i++) {
      setBatchRows(prev => prev.map((r, j) => j === i ? { ...r, status: 'uploading' } : r));
      try {
        const r = await uploadOneFileAsTransfer(batchFiles[i], (pct) => {
          setBatchRows(prev => prev.map((row, j) => j === i ? { ...row, progress: pct } : row));
        });
        setBatchRows(prev => prev.map((row, j) => j === i
          ? { ...row, status: 'done', progress: 100, shareLink: r.shareLink }
          : row
        ));
      } catch (e) {
        const msg = e?.response ? formatApiError(e) : (e.message || 'Upload failed');
        setBatchRows(prev => prev.map((row, j) => j === i
          ? { ...row, status: 'error', error: msg }
          : row
        ));
      }
    }
    setBatchBusy(false);
    toast.success('Batch complete');
  };

  const resetBatch = () => {
    setBatchFiles([]);
    setBatchRows([]);
    setBatchBusy(false);
    setQrModal(null);
    if (batchInputRef.current) batchInputRef.current.value = '';
  };

  const setBatchRowWa = (idx, val) => {
    setBatchRows(prev => prev.map((r, j) => j === idx ? { ...r, waNumber: val } : r));
  };

  // ---------- RENDER ----------
  return (
    <div className="space-y-4" data-testid="event-mode-panel">
      {/* Sub-mode selector */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setMode('single')}
          className={`px-4 py-3 rounded-lg border text-left transition ${
            mode === 'single'
              ? 'border-[color:var(--event-accent)] bg-[color:var(--event-accent-soft)]'
              : 'border-white/10 bg-white/[0.02] hover:border-white/30'
          }`}
          style={{ '--event-accent': brand.accent, '--event-accent-soft': brand.accentSoft }}
          data-testid="event-mode-single-tab">
          <div className="font-medium flex items-center gap-2"><Camera className="w-4 h-4" /> Single photo</div>
          <div className="text-xs text-white/50 mt-1">One at a time · instant QR</div>
        </button>
        <button
          type="button"
          onClick={() => setMode('batch')}
          className={`px-4 py-3 rounded-lg border text-left transition ${
            mode === 'batch'
              ? 'border-[color:var(--event-accent)] bg-[color:var(--event-accent-soft)]'
              : 'border-white/10 bg-white/[0.02] hover:border-white/30'
          }`}
          style={{ '--event-accent': brand.accent, '--event-accent-soft': brand.accentSoft }}
          data-testid="event-mode-batch-tab">
          <div className="font-medium flex items-center gap-2"><Upload className="w-4 h-4" /> Batch</div>
          <div className="text-xs text-white/50 mt-1">Many photos · one QR each</div>
        </button>
      </div>

      {mode === 'single' && (
        <div className="space-y-4" data-testid="event-single-pane">
          {!singleResult && (
            <>
              <div
                onClick={() => !singleBusy && singleInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 sm:p-10 text-center transition ${singleBusy ? 'opacity-60' : 'cursor-pointer hover:border-white/30'} border-white/15`}
                data-testid="event-single-dropzone">
                <Camera className="w-10 h-10 mx-auto text-white/40 mb-3" />
                <div className="font-medium">{singleFile ? singleFile.name : 'Take a photo or pick a file'}</div>
                <div className="text-xs text-white/50 mt-1">
                  {singleFile ? formatSize(singleFile.size) : 'Camera capture supported on phones · one photo at a time'}
                </div>
                <input
                  ref={singleInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => setSingleFile(e.target.files?.[0] || null)}
                  data-testid="event-single-input"
                />
              </div>

              {singleBusy && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-white/60">
                    <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</span>
                    <span className="font-mono" style={{ color: brand.accent }}>{singleProgress}%</span>
                  </div>
                  <Progress value={singleProgress} className="h-2" />
                </div>
              )}

              {singleError && (
                <div className="p-3 border rounded-lg text-sm flex items-start gap-2 border-rose-500/30 bg-rose-500/5 text-rose-300" data-testid="event-single-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">{singleError}</div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                {singleFile && !singleBusy && (
                  <Button variant="outline" onClick={resetSingle} data-testid="event-single-clear">Clear</Button>
                )}
                <Button
                  onClick={startSingleUpload}
                  disabled={!singleFile || singleBusy}
                  style={{ background: brand.accent, color: brand.accentText }}
                  data-testid="event-single-upload">
                  <Upload className="w-4 h-4 mr-2" />
                  {singleBusy ? 'Uploading…' : 'Upload & generate QR'}
                </Button>
              </div>
            </>
          )}

          {singleResult && (
            <div className="space-y-3">
              <QRCard
                shareLink={singleResult.shareLink}
                fileName={singleResult.fileName}
                brand={brand}
              />
              <div className="flex gap-2 justify-center">
                <Button onClick={resetSingle} className="w-full sm:w-auto" style={{ background: brand.accent, color: brand.accentText }} data-testid="event-single-next">
                  <RotateCw className="w-4 h-4 mr-2" /> Next guest
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'batch' && (
        <div className="space-y-4" data-testid="event-batch-pane">
          {batchRows.length === 0 && (
            <>
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={async (e) => { e.preventDefault(); stageBatch(await filesFromDrop(e.dataTransfer)); }}
                onClick={() => batchInputRef.current?.click()}
                className="border-2 border-dashed rounded-xl p-6 sm:p-10 text-center cursor-pointer transition border-white/15 hover:border-white/30"
                data-testid="event-batch-dropzone">
                <Upload className="w-10 h-10 mx-auto text-white/40 mb-3" />
                <div className="font-medium">Drop photos or folders, or click to browse</div>
                <div className="text-xs text-white/50 mt-1">Every photo becomes its own QR / share link · 1-day expiry</div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); batchFolderInputRef.current?.click(); }}
                  className="mt-3 text-xs px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:border-white/30"
                  data-testid="event-batch-folder-btn">
                  Choose a folder
                </button>
                <input
                  ref={batchInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => stageBatch(e.target.files)}
                  data-testid="event-batch-input"
                />
                <input
                  ref={batchFolderInputRef}
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  className="hidden"
                  onChange={(e) => { stageBatch(e.target.files); e.target.value = ''; }}
                  data-testid="event-batch-folder-input"
                />
              </div>

              {batchFiles.length > 0 && (
                <div className="space-y-1.5" data-testid="event-batch-file-list">
                  {batchFiles.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center justify-between text-sm bg-white/5 rounded px-3 py-2">
                      <span className="truncate">{f.name} <span className="text-white/40">· {formatSize(f.size)}</span></span>
                      <button onClick={() => removeBatchFile(i)} className="text-white/50 hover:text-white p-1" data-testid={`event-batch-remove-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={startBatchUpload}
                  disabled={batchFiles.length === 0 || batchBusy}
                  style={{ background: brand.accent, color: brand.accentText }}
                  data-testid="event-batch-upload">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {batchFiles.length} photo{batchFiles.length === 1 ? '' : 's'} (individual QRs)
                </Button>
              </div>
            </>
          )}

          {batchRows.length > 0 && (
            <div className="space-y-2" data-testid="event-batch-rows">
              {batchRows.map((r, i) => (
                <div key={`row-${i}-${r.fileName}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex flex-col sm:flex-row gap-3 items-start" data-testid={`event-batch-row-${i}`}>
                  <div className="flex-1 min-w-0 w-full">
                    <div className="text-sm font-medium truncate">{r.fileName}</div>
                    <div className="text-xs text-white/50 mt-0.5">{formatSize(r.fileSize)}</div>
                    {r.status === 'uploading' && (
                      <div className="mt-2 h-1 bg-white/10 rounded overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${r.progress}%`, background: brand.accent }} />
                      </div>
                    )}
                    {r.status === 'error' && (
                      <div className="text-xs text-rose-400 mt-1 truncate" title={r.error}>{r.error}</div>
                    )}
                    {r.status === 'done' && r.shareLink && (
                      <div className="flex flex-wrap gap-2 mt-2 items-center">
                        <Input readOnly value={r.shareLink} className="bg-black/40 border-white/10 font-mono text-[11px] h-8 flex-1 min-w-[180px]" data-testid={`event-batch-link-${i}`} />
                        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(r.shareLink); toast.success('Link copied'); }} className="h-8" data-testid={`event-batch-copy-${i}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setQrModal(r)} className="h-8" data-testid={`event-batch-qr-${i}`}>
                          <QrCode className="w-3.5 h-3.5 mr-1" /> QR
                        </Button>
                        <Input
                          value={r.waNumber}
                          onChange={(e) => setBatchRowWa(i, e.target.value)}
                          placeholder="Phone e.g. +91…"
                          className="bg-white/5 border-white/10 h-8 text-xs w-40"
                          data-testid={`event-batch-wa-num-${i}`}
                        />
                        <Button
                          size="sm"
                          disabled={!r.waNumber.replace(/\D/g, '')}
                          onClick={() => {
                            const wa = waLink(r.waNumber, `Here's your photo: ${r.shareLink}`);
                            if (wa) window.open(wa, '_blank', 'noopener,noreferrer');
                          }}
                          className="h-8"
                          style={{ background: brand.accent, color: brand.accentText }}
                          data-testid={`event-batch-wa-${i}`}>
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Send
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className={`shrink-0 text-xs font-medium ${
                    r.status === 'done' ? 'text-emerald-400' :
                    r.status === 'error' ? 'text-rose-400' :
                    r.status === 'uploading' ? 'text-white' :
                    'text-white/50'
                  }`}>
                    {r.status === 'done' && <><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Done</>}
                    {r.status === 'error' && <><AlertCircle className="w-3.5 h-3.5 inline mr-1" />Failed</>}
                    {r.status === 'uploading' && `${r.progress}%`}
                    {r.status === 'pending' && 'Queued'}
                  </div>
                </div>
              ))}

              <div className="flex justify-end pt-1">
                <Button variant="outline" onClick={resetBatch} disabled={batchBusy} data-testid="event-batch-reset">
                  <RotateCw className="w-4 h-4 mr-2" /> New batch
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Enlarged QR modal (batch mode) */}
      {qrModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setQrModal(null)}>
          <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-5">
              <QRCard
                shareLink={qrModal.shareLink}
                fileName={qrModal.fileName}
                brand={brand}
                onDismiss={() => setQrModal(null)}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EventModePanel;
